import React, { useState, useEffect } from 'react';
import { Project, OnboardingAnswer, MilestoneStatus, ProjectStatus } from '../types';
import { Button, Input, Card } from './UIComponents';
import { ArrowRight, CheckCircle2, LayoutGrid, Shield, Clock, FileText, Activity, AlertCircle } from 'lucide-react';

interface ReceiverOnboardingProps {
  project: Project;
  onComplete: (answers: OnboardingAnswer[]) => void;
}

type Step = 'welcome' | 'questions' | 'timeline' | 'auth';

export const ReceiverOnboarding: React.FC<ReceiverOnboardingProps> = ({ project, onComplete }) => {
  const [step, setStep] = useState<Step>('welcome');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [receiverName, setReceiverName] = useState(project.receiverName || '');
  const [password, setPassword] = useState('');

  const questions = project.onboardingQuestions || [];
  const isDraft = project.status === ProjectStatus.DRAFT;

  const handleAnswerChange = (qId: string, text: string) => {
    setAnswers(prev => ({ ...prev, [qId]: text }));
  };

  const handleNext = () => {
    if (step === 'welcome') {
      if (questions.length > 0) {
        setStep('questions');
      } else {
        setStep('timeline');
      }
    }
    else if (step === 'questions') setStep('timeline');
    else if (step === 'timeline') setStep('auth');
  };

  const handleSubmit = () => {
    const formattedAnswers: OnboardingAnswer[] = questions.map(q => ({
      questionId: q.id,
      questionText: q.text,
      answerText: answers[q.id] || ''
    }));
    onComplete(formattedAnswers);
  };

  // --- WELCOME SCREEN ---
  if (step === 'welcome') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6 animate-fade-in">
        <div className="max-w-md w-full text-center">
          <div className="w-12 h-12 bg-zinc-900 rounded-xl flex items-center justify-center mx-auto mb-6 text-zinc-100 shadow-glow border border-zinc-800">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 5L12 19L19 5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <p className="text-zinc-500 font-medium text-sm uppercase tracking-wide mb-2">Verline Studio invites you to join</p>
          <h1 className="text-3xl font-medium text-white mb-6">{project.name}</h1>
          <p className="text-zinc-400 mb-6 leading-relaxed">
            We’ve set up a dedicated console for this project.
            {questions.length > 0 ? "Before we begin, we have a few quick questions to align on goals and context." : "Log in to view your live project timeline and progress."}
          </p>

          {isDraft && (
            <div className="bg-zinc-900/50 rounded-lg p-3 mb-10 border border-zinc-800 text-[12px] text-zinc-500">
              We’ll finalize the project timeline after you answer a few quick questions.
            </div>
          )}

          <Button size="lg" className="w-full bg-zinc-100 text-black hover:bg-white border-0" onClick={handleNext}>
            {questions.length > 0 ? "Start Project Setup" : "View Project"}
          </Button>
          <p className="mt-6 text-xs text-zinc-600">{questions.length > 0 ? "Takes about 2 minutes" : "Instant access"}</p>
        </div>
      </div>
    );
  }

  // --- QUESTIONNAIRE SCREEN ---
  if (step === 'questions') {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6 animate-fade-in">
        <div className="max-w-lg w-full">
          <div className="mb-8 flex items-center justify-between border-b border-zinc-900 pb-4">
            <h2 className="text-xl font-medium text-zinc-100">Project Context</h2>
            <span className="text-xs text-zinc-400 bg-zinc-900 px-2 py-1 rounded">Step 1 of 3</span>
          </div>

          <div className="space-y-6">
            {questions.length === 0 ? (
              <div className="text-zinc-600 italic text-center py-8">No specific questions for this project.</div>
            ) : (
              questions.map(q => (
                <div key={q.id} className="space-y-2">
                  <label className="block text-sm font-medium text-zinc-300">
                    {q.text} {q.required && <span className="text-rose-500">*</span>}
                  </label>
                  <textarea
                    className="w-full h-24 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-md text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-zinc-600 focus:ring-1 focus:ring-zinc-600 transition-colors resize-none shadow-inner"
                    placeholder="Type your answer here..."
                    value={answers[q.id] || ''}
                    onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                  />
                </div>
              ))
            )}
          </div>

          <div className="mt-10 flex justify-end">
            <Button size="lg" onClick={handleNext}>
              Next: Review Timeline <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // --- TIMELINE PREVIEW SCREEN ---
  if (step === 'timeline') {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6 animate-fade-in">
        <div className="max-w-lg w-full">
          <div className="mb-8 flex items-center justify-between border-b border-zinc-900 pb-4">
            <h2 className="text-xl font-medium text-zinc-100">Project Roadmap</h2>
            <span className="text-xs text-zinc-400 bg-zinc-900 px-2 py-1 rounded">Step 2 of 3</span>
          </div>

          {/* Draft Notice vs Live Notice */}
          {isDraft ? (
            <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-4 mb-6 flex gap-3">
              <AlertCircle size={18} className="text-zinc-500 mt-0.5" />
              <div>
                <h4 className="text-[13px] font-medium text-zinc-200">Draft timeline</h4>
                <p className="text-[12px] text-zinc-500 leading-relaxed mt-1">
                  These milestones are a starting point. The final timeline will be confirmed by the provider after reviewing your answers.
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-emerald-950/20 border border-emerald-900/30 rounded-lg p-4 mb-6 flex gap-3">
              <CheckCircle2 size={18} className="text-emerald-500 mt-0.5" />
              <div>
                <h4 className="text-[13px] font-medium text-emerald-100">Live Project Plan</h4>
                <p className="text-[12px] text-emerald-500/80 leading-relaxed mt-1">
                  This is the active timeline for the project. You'll be able to track progress here.
                </p>
              </div>
            </div>
          )}

          <div className="bg-zinc-950 rounded-xl border border-zinc-800 p-6 mb-8 max-h-[50vh] overflow-y-auto">
            <div className="space-y-6 relative pl-2">
              <div className="absolute left-[9px] top-2 bottom-2 w-px bg-zinc-800 border-l border-dashed border-zinc-700 opacity-50" />
              {project.milestones.map((m, idx) => (
                <div key={m.id} className={`relative flex gap-4 items-start ${m.status === MilestoneStatus.DRAFT ? 'opacity-70' : ''}`}>
                  <div className={`relative z-10 w-5 h-5 rounded-full border flex items-center justify-center text-[10px] font-medium ${m.status === MilestoneStatus.IN_PROGRESS
                    ? 'bg-emerald-500 border-emerald-500 text-black'
                    : 'bg-zinc-950 border-zinc-700 text-zinc-500'
                    }`}>
                    {m.status === MilestoneStatus.IN_PROGRESS ? <Activity size={10} /> : idx + 1}
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-zinc-300">{m.title}</h3>
                    <p className="text-xs text-zinc-600 mt-1">
                      Status: {isDraft ? 'Pending Finalization' : m.status.replace('_', ' ').toLowerCase()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <Button size="lg" onClick={handleNext}>
              Looks Good, Let's Join <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // --- AUTH / JOIN SCREEN ---
  if (step === 'auth') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6 animate-fade-in">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-12 h-12 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-4 text-emerald-500 border border-emerald-500/20">
              <CheckCircle2 size={24} />
            </div>
            <h2 className="text-2xl font-medium text-zinc-100 mb-2">You're all set!</h2>
            <p className="text-zinc-500">Create your secure access to view the project console.</p>
          </div>

          <div className="bg-zinc-950 p-6 rounded-xl border border-zinc-800 space-y-4 mb-6">
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1.5">Your Name</label>
              <Input value={receiverName} onChange={(e) => setReceiverName(e.target.value)} placeholder="Jane Doe" />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1.5">Create Password</label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
            </div>
          </div>

          <Button size="lg" className="w-full bg-zinc-100 text-black hover:bg-white border-0" onClick={handleSubmit} disabled={!receiverName || !password}>
            Access Project Console
          </Button>
          <p className="mt-4 text-center text-xs text-zinc-600">
            By joining, you agree to our Terms of Service.
          </p>
        </div>
      </div>
    );
  }

  return null;
};